import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { videosAPI, coursesAPI, paymentAPI, progressAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Loader2, Lock, Play, ChevronLeft, CheckCircle, ShoppingCart, RotateCcw } from 'lucide-react';
import { formatPrice, extractInstructor } from '../lib/utils';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

// Format seconds to MM:SS
const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const VideoPage = () => {
    const { id } = useParams();
    const { isAuthenticated } = useAuth();
    const [video, setVideo] = useState(null);
    const [course, setCourse] = useState(null);
    const [hasAccess, setHasAccess] = useState(false);
    const [embedUrl, setEmbedUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(false);
    const [error, setError] = useState('');
    
    // Progress tracking state
    const [savedProgress, setSavedProgress] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const iframeRef = useRef(null);
    const playerRef = useRef(null);
    const saveIntervalRef = useRef(null);

    const handlePurchase = async () => {
        if (!isAuthenticated) {
            toast.error('Please sign in to purchase');
            return;
        }
        
        setPurchasing(true);
        try {
            const response = await paymentAPI.createCheckout(parseInt(id));
            window.location.href = response.data.url;
        } catch (err) {
            console.error('Purchase error:', err);
            const message = err.response?.data?.detail || 'Failed to start checkout';
            toast.error(message);
            setPurchasing(false);
        }
    };

    const handleUpgrade = async (packageId) => {
        setPurchasing(true);
        try {
            const response = await paymentAPI.createCheckout(packageId);
            window.location.href = response.data.url;
        } catch (err) {
            const message = err.response?.data?.detail || 'Failed to start checkout';
            toast.error(message);
            setPurchasing(false);
        }
    };

    // Save progress to backend
    const saveProgress = useCallback(async (progressSeconds, durationSeconds) => {
        if (!isAuthenticated || !hasAccess || durationSeconds <= 0) return;
        
        try {
            await progressAPI.updateProgress(parseInt(id), Math.floor(progressSeconds), Math.floor(durationSeconds));
        } catch (err) {
            console.error('Failed to save progress:', err);
        }
    }, [id, isAuthenticated, hasAccess]);

    // Initialize Vimeo Player
    useEffect(() => {
        if (!embedUrl || !iframeRef.current) return;

        const initPlayer = async () => {
            try {
                // Load Vimeo Player API if not already loaded
                if (!window.Vimeo) {
                    const script = document.createElement('script');
                    script.src = 'https://player.vimeo.com/api/player.js';
                    script.async = true;
                    await new Promise((resolve, reject) => {
                        script.onload = resolve;
                        script.onerror = reject;
                        document.body.appendChild(script);
                    });
                }

                const player = new window.Vimeo.Player(iframeRef.current);
                playerRef.current = player;

                // Get video duration
                const dur = await player.getDuration();
                setDuration(dur);

                // Resume from saved position if available
                if (savedProgress && savedProgress.progress_seconds > 0) {
                    await player.setCurrentTime(savedProgress.progress_seconds);
                    toast.info(`Resuming from ${formatTime(savedProgress.progress_seconds)}`);
                }

                // Track time updates
                player.on('timeupdate', (data) => {
                    setCurrentTime(data.seconds);
                });

                // Save progress every 30 seconds while playing
                player.on('play', () => {
                    saveIntervalRef.current = setInterval(async () => {
                        const time = await player.getCurrentTime();
                        const dur = await player.getDuration();
                        saveProgress(time, dur);
                    }, 30000);
                });

                player.on('pause', async () => {
                    if (saveIntervalRef.current) {
                        clearInterval(saveIntervalRef.current);
                    }
                    // Save on pause
                    const time = await player.getCurrentTime();
                    const dur = await player.getDuration();
                    saveProgress(time, dur);
                });

                player.on('ended', async () => {
                    if (saveIntervalRef.current) {
                        clearInterval(saveIntervalRef.current);
                    }
                    // Save completion
                    const dur = await player.getDuration();
                    saveProgress(dur, dur);
                });

            } catch (err) {
                console.error('Failed to initialize Vimeo player:', err);
            }
        };

        initPlayer();

        return () => {
            if (saveIntervalRef.current) {
                clearInterval(saveIntervalRef.current);
            }
        };
    }, [embedUrl, savedProgress, saveProgress]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError('');
            
            try {
                // Fetch video details
                const videoRes = await videosAPI.getById(id);
                setVideo(videoRes.data);

                // If it's a course lesson, fetch course data
                if (videoRes.data.course_id) {
                    try {
                        const courseRes = await coursesAPI.getById(videoRes.data.course_id);
                        setCourse(courseRes.data);
                    } catch (err) {
                        console.log('Could not fetch course data');
                    }
                }

                // Check access if authenticated
                if (isAuthenticated) {
                    try {
                        const accessRes = await videosAPI.checkAccess(id);
                        setHasAccess(accessRes.data.has_access);

                        if (accessRes.data.has_access) {
                            const embedRes = await videosAPI.getEmbed(id);
                            setEmbedUrl(embedRes.data.embed_url);
                            
                            // Fetch saved progress
                            try {
                                const progressRes = await progressAPI.getProgress(id);
                                if (progressRes.data.progress_seconds > 0) {
                                    setSavedProgress(progressRes.data);
                                }
                            } catch (err) {
                                console.log('No saved progress');
                            }
                        }
                    } catch (err) {
                        setHasAccess(false);
                    }
                }
            } catch (err) {
                setError('Failed to load video');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, isAuthenticated]);

    const handleResetProgress = async () => {
        try {
            await progressAPI.resetProgress(parseInt(id));
            setSavedProgress(null);
            setCurrentTime(0);
            if (playerRef.current) {
                playerRef.current.setCurrentTime(0);
            }
            toast.success('Progress reset - starting from beginning');
        } catch (err) {
            toast.error('Failed to reset progress');
        }
    };

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center" data-testid="video-loading">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    if (error || !video) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center" data-testid="video-error">
                <div className="text-center">
                    <p className="text-destructive mb-4">{error || 'Video not found'}</p>
                    <Link to="/" className="text-accent hover:underline">Go home</Link>
                </div>
            </div>
        );
    }

    const instructor = video.instructor || extractInstructor(video.title);
    const isCourseLesson = !!video.course_id && video.lesson_number;
    const isSculpture = video.type === 'sculpture';
    const backLink = isCourseLesson 
        ? `/courses/${video.course_id}` 
        : isSculpture 
            ? '/sculptures' 
            : '/workshops';
    const backText = isCourseLesson 
        ? 'Back to Course' 
        : isSculpture 
            ? 'Back to Sculpture Videos' 
            : 'Back to Painting Videos';

    const progressPercent = duration > 0 ? Math.round((currentTime / duration) * 100) : 0;

    // Upgrade nudge: find the best package to offer when lesson is tier-locked
    const starterPkg = course?.packages?.find(p => p.package_type === 'starter');
    const upgradePkg = course?.packages?.find(p => p.package_type === 'upgrade');
    const completePkg = course?.packages?.find(p => p.package_type === 'complete');
    // Lesson is in starter tier → user needs to buy from scratch → show complete
    // Lesson is beyond starter tier → user may own starter → show upgrade path
    const lessonInStarter = starterPkg?.includes_lessons?.includes(video?.lesson_number);
    const upsellPkg = lessonInStarter
        ? (completePkg || upgradePkg)
        : (upgradePkg || completePkg);
    const showUpgradeNudge = isAuthenticated && !hasAccess && isCourseLesson && !!upsellPkg;

    // Generate SEO description
    const seoDescription = video.description 
        ? video.description.substring(0, 155) + '...'
        : `Watch ${video.title} by ${instructor}. Professional art instruction video from Zarolla Online Art Academy.`;

    // Generate SEO keywords
    const seoKeywords = [
        'online art classes',
        instructor ? `${instructor} teaching` : null,
        video.title.toLowerCase().includes('alla prima') ? 'alla prima painting' : null,
        video.title.toLowerCase().includes('portrait') ? 'portrait painting tutorial' : null,
        video.type === 'workshop' ? 'painting workshop video' : null,
        video.type === 'sculpture' ? 'sculpture tutorial' : null,
    ].filter(Boolean);

    return (
        <div className="min-h-screen bg-background" data-testid="video-page">
            {/* Dynamic SEO with Video Schema */}
            <SEO
                title={video.title}
                description={seoDescription}
                image={video.thumbnail}
                url={`/video/${id}`}
                keywords={seoKeywords}
                video={{
                    title: video.title,
                    description: video.description,
                    thumbnail: video.thumbnail,
                    vimeo_id: video.vimeo_id,
                    duration: video.duration,
                    created_at: video.created_at,
                }}
            />
            <div className="container-main py-8">
                {/* Back link */}
                <Link 
                    to={backLink}
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
                    data-testid="back-link"
                >
                    <ChevronLeft className="h-4 w-4" />
                    {backText}
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2">
                        {/* Video Player */}
                        <div className="relative aspect-video bg-black rounded-sm overflow-hidden" data-testid="video-player-container">
                            {hasAccess && embedUrl ? (
                                <iframe
                                    ref={iframeRef}
                                    src={embedUrl}
                                    className="absolute inset-0 w-full h-full"
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture"
                                    allowFullScreen
                                    title={video.title}
                                    data-testid="vimeo-iframe"
                                />
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center video-lock-overlay">
                                    <Lock className="h-12 w-12 text-white/60 mb-4" />
                                    <p className="text-white/80 font-medium mb-2">
                                        {isAuthenticated ? 'Purchase Required' : 'Sign in to access'}
                                    </p>
                                    <p className="text-white/60 text-sm mb-6">
                                        {isAuthenticated 
                                            ? 'You need to purchase this video to watch it'
                                            : 'Sign in to check if you have access to this video'
                                        }
                                    </p>
                                    {!isAuthenticated && (
                                        <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90">
                                            <Link to="/login" data-testid="sign-in-to-watch">Sign In</Link>
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Upgrade nudge — tier-locked lesson */}
                        {showUpgradeNudge && (
                            <div
                                className="mt-4 px-5 py-4 bg-secondary/60 border border-accent/25 rounded-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                                data-testid="upgrade-nudge"
                            >
                                <div>
                                    <p className="text-sm font-semibold text-primary">
                                        Want to continue?
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {upsellPkg?.package_type === 'upgrade'
                                            ? `Unlock the remaining lessons — ${formatPrice(upsellPkg.cost)}`
                                            : `Unlock the complete course — all ${course?.lessons?.length || ''} lessons for ${formatPrice(upsellPkg?.cost)}`}
                                    </p>
                                </div>
                                <Button
                                    size="sm"
                                    className="flex-shrink-0 bg-accent text-accent-foreground hover:bg-accent/90 text-xs px-4"
                                    onClick={() => handleUpgrade(upsellPkg.id)}
                                    disabled={purchasing}
                                    data-testid="upgrade-nudge-btn"
                                >
                                    {purchasing
                                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        : `Unlock — ${formatPrice(upsellPkg.cost)}`}
                                </Button>
                            </div>
                        )}

                        {/* Progress Bar */}
                        {hasAccess && duration > 0 && (
                            <div className="mt-3" data-testid="progress-bar-container">
                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                    <span>{formatTime(currentTime)}</span>
                                    <div className="flex items-center gap-2">
                                        <span>{progressPercent}% watched</span>
                                        {savedProgress && savedProgress.progress_seconds > 0 && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 px-2 text-xs"
                                                onClick={handleResetProgress}
                                                data-testid="reset-progress-btn"
                                            >
                                                <RotateCcw className="h-3 w-3 mr-1" />
                                                Start Over
                                            </Button>
                                        )}
                                    </div>
                                    <span>{formatTime(duration)}</span>
                                </div>
                                <div className="h-1 bg-secondary rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-accent transition-all duration-300"
                                        style={{ width: `${progressPercent}%` }}
                                        data-testid="progress-bar"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Video Info */}
                        <div className="mt-6">
                            <h1 className="font-serif text-2xl md:text-3xl font-semibold" data-testid="video-title">
                                {video.title}
                            </h1>
                            {instructor && (
                                <p className="text-muted-foreground mt-2" data-testid="video-instructor">
                                    with {instructor}
                                </p>
                            )}
                            {video.description && (
                                <div className="mt-6" data-testid="video-description">
                                    <h2 className="font-serif text-xl font-medium mb-4">About This Video</h2>
                                    <div className="text-muted-foreground leading-relaxed space-y-4">
                                        {video.description.split('\n\n').map((paragraph, index) => (
                                            <p key={index}>{paragraph}</p>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1">
                        {/* Course Lessons Sidebar */}
                        {course && course.lessons && course.lessons.length > 0 && (
                            <Card className="p-4" data-testid="lessons-sidebar">
                                <h3 className="font-serif text-lg font-medium mb-4">
                                    Course Lessons
                                </h3>
                                <ScrollArea className="h-[400px]">
                                    <div className="space-y-1 pr-4">
                                        {course.lessons.map((lesson, index) => (
                                            <Link
                                                key={lesson.id}
                                                to={`/video/${lesson.id}`}
                                                className={`block p-3 rounded transition-colors ${
                                                    lesson.id === parseInt(id)
                                                        ? 'bg-accent/10 border-l-2 border-accent'
                                                        : 'hover:bg-secondary'
                                                }`}
                                                data-testid={`lesson-link-${lesson.id}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs font-medium text-muted-foreground w-6">
                                                        {String(lesson.lesson_number || index + 1).padStart(2, '0')}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium truncate">
                                                            Session {lesson.lesson_number || index + 1}
                                                        </p>
                                                    </div>
                                                    {lesson.id === parseInt(id) && (
                                                        <Play className="h-4 w-4 text-accent" />
                                                    )}
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </ScrollArea>
                            </Card>
                        )}

                        {/* Purchase/Access Info */}
                        {!course && video.cost > 0 && (
                            <Card className="p-6" data-testid="purchase-card">
                                <div className="text-center">
                                    <p className="text-2xl font-semibold">{formatPrice(video.cost)}</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        Lifetime access
                                    </p>
                                </div>
                                {hasAccess ? (
                                    <div className="mt-4 flex items-center justify-center gap-2 text-green-600">
                                        <CheckCircle className="h-5 w-5" />
                                        <span className="font-medium">You own this video</span>
                                    </div>
                                ) : !isAuthenticated ? (
                                    <Button asChild className="w-full mt-4 bg-accent text-accent-foreground hover:bg-accent/90">
                                        <Link to="/login" data-testid="sign-in-to-purchase">
                                            Sign In to Purchase
                                        </Link>
                                    </Button>
                                ) : (
                                    <Button 
                                        className="w-full mt-4 bg-accent text-accent-foreground hover:bg-accent/90"
                                        onClick={handlePurchase}
                                        disabled={purchasing}
                                        data-testid="buy-video-btn"
                                    >
                                        {purchasing ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                            <ShoppingCart className="h-4 w-4 mr-2" />
                                        )}
                                        Buy Now
                                    </Button>
                                )}
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoPage;
